/**
 * Worklog v2.0.0 walkthrough tour registry and first-run ordering.
 * @module features/walkthrough/tours/tourRegistry
 */

import { buildNavContext, isMyTimesheetNavVisible } from '../../../navigation/navConfig';
import {
  resolveAssignedRoles,
  resolvePrimaryRole,
  ROLE_CODES,
} from '../../../utils/rolePriority';
import { tourMeta } from '../i18n/walkthroughI18n';
import { filterStepsForUser } from '../walkthroughPolicy';
import { COMMON_STEPS } from './commonSteps';
import { EMPLOYEE_TOUR } from './employeeTour';
import { PM_TOUR } from './pmTour';
import { RM_TOUR } from './rmTour';
import { ADMIN_TOUR } from './adminTour';
import { CSG_TOUR } from './csgTour';
import { SKILLS_TOUR } from './skillsTour';
import { AUDIT_LOG_TOUR, resolveAuditLogPrerequisiteRoute } from './auditLogTour';

const gettingStartedMeta = tourMeta('getting-started');

/** @type {import('../walkthroughTypes').WalkthroughTourDef} */
export const GETTING_STARTED_TOUR = {
  id: 'getting-started',
  title: gettingStartedMeta.title,
  description: gettingStartedMeta.description,
  roles: ['employee', 'pm', 'rm', 'csg', 'admin', 'dh'],
  priority: 10,
  version: 2,
  steps: COMMON_STEPS,
};

/** @type {Record<string, import('../walkthroughTypes').WalkthroughTourDef>} */
export const TOUR_REGISTRY = Object.freeze({
  [GETTING_STARTED_TOUR.id]: GETTING_STARTED_TOUR,
  [EMPLOYEE_TOUR.id]: EMPLOYEE_TOUR,
  [PM_TOUR.id]: PM_TOUR,
  [RM_TOUR.id]: RM_TOUR,
  [ADMIN_TOUR.id]: ADMIN_TOUR,
  [CSG_TOUR.id]: CSG_TOUR,
  [SKILLS_TOUR.id]: SKILLS_TOUR,
  [AUDIT_LOG_TOUR.id]: AUDIT_LOG_TOUR,
});

/** @type {import('../walkthroughTypes').WalkthroughTourDef[]} */
export const ALL_WALKTHROUGH_TOURS = Object.values(TOUR_REGISTRY);

/** @type {Record<string, string>} */
const PRIMARY_TOUR_BY_ROLE = Object.freeze({
  [ROLE_CODES.EMPLOYEE]: EMPLOYEE_TOUR.id,
  [ROLE_CODES.RM]: RM_TOUR.id,
  [ROLE_CODES.PM]: PM_TOUR.id,
  [ROLE_CODES.CSG]: CSG_TOUR.id,
  [ROLE_CODES.ADMIN]: ADMIN_TOUR.id,
  [ROLE_CODES.DH]: ADMIN_TOUR.id,
});

/** Secondary role mini-tours (lower priority than primary workflow). */
const SECONDARY_ROLE_TOUR_ORDER = Object.freeze([
  { role: ROLE_CODES.PM, tourId: PM_TOUR.id },
  { role: ROLE_CODES.CSG, tourId: CSG_TOUR.id },
  { role: ROLE_CODES.RM, tourId: RM_TOUR.id },
]);

/**
 * PM+CSG users run the PM chapter first, then an optional CSG continuation.
 *
 * @param {object} user
 * @param {import('../../../navigation/navConfig').NavContext} ctx
 * @param {string} primaryRole
 * @returns {string | undefined}
 */
function resolveWalkthroughPrimaryTourId(user, ctx, primaryRole) {
  if (primaryRole === ROLE_CODES.CSG && user.isProjectManager && ctx.showCsgNav) {
    return PM_TOUR.id;
  }
  return PRIMARY_TOUR_BY_ROLE[primaryRole];
}

/**
 * @param {import('../walkthroughTypes').WalkthroughTourDef} tour
 * @param {object} user
 * @returns {import('../walkthroughTypes').WalkthroughTourDef}
 */
function withDynamicPrerequisite(tour, user) {
  if (tour.id === AUDIT_LOG_TOUR.id) {
    return { ...tour, prerequisiteRoute: resolveAuditLogPrerequisiteRoute(user) };
  }
  return tour;
}

/**
 * @param {import('../walkthroughTypes').WalkthroughTourDef} tour
 * @param {object|null|undefined} user
 * @param {import('../../../navigation/navConfig').NavContext} ctx
 * @returns {import('../walkthroughTypes').ResolvedWalkthroughTour | null}
 */
function resolveTourForUser(tour, user, ctx) {
  if (tour.visible && !tour.visible(user, ctx)) {
    return null;
  }

  const steps = filterStepsForUser(tour.steps, user, ctx);
  if (steps.length === 0) {
    return null;
  }

  return {
    ...withDynamicPrerequisite(tour, user),
    steps,
  };
}

/**
 * Ordered tour list for a user's first-run walkthrough experience.
 * Primary role tour runs first, then secondary role mini-tours (e.g. PM after CSG).
 *
 * @param {object|null|undefined} user
 * @returns {import('../walkthroughTypes').ResolvedWalkthroughTour[]}
 */
export function getToursForUser(user) {
  if (!user) return [];

  const ctx = buildNavContext(user);
  const primaryRole = resolvePrimaryRole(user);
  const assignedRoles = resolveAssignedRoles(user);
  const walkthroughPrimaryTourId = resolveWalkthroughPrimaryTourId(user, ctx, primaryRole);
  const walkthroughPrimaryRole =
    walkthroughPrimaryTourId === PM_TOUR.id
      ? ROLE_CODES.PM
      : walkthroughPrimaryTourId === CSG_TOUR.id
        ? ROLE_CODES.CSG
        : walkthroughPrimaryTourId === RM_TOUR.id
          ? ROLE_CODES.RM
          : walkthroughPrimaryTourId === ADMIN_TOUR.id
            ? ROLE_CODES.ADMIN
            : walkthroughPrimaryTourId === EMPLOYEE_TOUR.id
              ? ROLE_CODES.EMPLOYEE
              : primaryRole;

  /** @type {string[]} */
  const orderedTourIds = [GETTING_STARTED_TOUR.id];

  if (walkthroughPrimaryTourId) {
    orderedTourIds.push(walkthroughPrimaryTourId);
  }

  for (const { role, tourId } of SECONDARY_ROLE_TOUR_ORDER) {
    const roleActive =
      role === ROLE_CODES.CSG
        ? ctx.showCsgNav
        : role === ROLE_CODES.PM
          ? !!user.isProjectManager
          : role === ROLE_CODES.RM
            ? ctx.rmOnlyNav
            : assignedRoles.includes(role);

    if (role !== walkthroughPrimaryRole && roleActive && !orderedTourIds.includes(tourId)) {
      orderedTourIds.push(tourId);
    }
  }

  if (
    isMyTimesheetNavVisible(user, ctx) &&
    !user?.isProjectManager &&
    !ctx.rmOnlyNav &&
    !orderedTourIds.includes(EMPLOYEE_TOUR.id)
  ) {
    orderedTourIds.push(EMPLOYEE_TOUR.id);
  }

  if (!orderedTourIds.includes(SKILLS_TOUR.id)) {
    orderedTourIds.push(SKILLS_TOUR.id);
  }
  if (!orderedTourIds.includes(AUDIT_LOG_TOUR.id)) {
    orderedTourIds.push(AUDIT_LOG_TOUR.id);
  }

  return orderedTourIds
    .map((tourId) => TOUR_REGISTRY[tourId])
    .filter(Boolean)
    .map((tour) => resolveTourForUser(tour, user, ctx))
    .filter((tour) => tour !== null);
}

/**
 * @param {object|null|undefined} user
 * @param {string} tourId
 * @returns {import('../walkthroughTypes').ResolvedWalkthroughTour | null}
 */
export function getTourForUser(user, tourId) {
  const tour = TOUR_REGISTRY[tourId];
  if (!tour || !user) return null;
  return resolveTourForUser(tour, user, buildNavContext(user));
}
